/**
 * Normalize raw Epic Games API response.
 * @param {Array} rawGames - elements array from Epic API
 * @returns {Array}
 */
export function normalizeEpic(rawGames) {
  if (!Array.isArray(rawGames)) return [];
  return rawGames
    .filter(g => {
      const promo = g.promotions;
      if (!promo) return false;
      const offers = promo.promotionalOffers?.[0]?.promotionalOffers || [];
      return offers.some(o => o.discountSetting?.discountPercentage === 0);
    })
    .map(g => {
      const promo = g.promotions.promotionalOffers[0].promotionalOffers[0];
      const img =
        g.keyImages?.find(i => i.type === 'OfferImageWide' || i.type === 'DieselStoreFrontWide')?.url ||
        g.keyImages?.[0]?.url ||
        '';
      const slug = g.productSlug || g.urlSlug || g.id;
      return {
        id: 'epic_' + g.id,
        title: g.title,
        platform: 'epic',
        type: 'free-to-keep',
        thumbnail: img,
        claimUrl: 'https://store.epicgames.com/en-US/p/' + slug,
        availableFrom: promo.startDate || null,
        availableUntil: promo.endDate || null,
        originalPrice: g.price?.totalPrice?.fmtPrice?.originalPrice || null,
      };
    });
}

/**
 * Normalize raw Steam API response.
 * @param {Object} rawData - { response: { apps: [...] } }
 * @returns {Array}
 */
export function normalizeSteam(rawData) {
  if (!rawData) return [];
  const apps = rawData.response?.apps || [];
  return apps.map(app => ({
    id: 'steam_' + app.appid,
    title: app.name || 'Unknown',
    platform: 'steam',
    type: 'permanently-free',
    thumbnail: `https://cdn.akamai.steamstatic.com/steam/apps/${app.appid}/header.jpg`,
    claimUrl: 'https://store.steampowered.com/app/' + app.appid,
    availableFrom: null,
    availableUntil: null,
    originalPrice: null,
  }));
}

/**
 * Normalize raw GOG API response.
 * @param {Object} rawData - { products: [...] }
 * @returns {Array}
 */
export function normalizeGOG(rawData) {
  if (!rawData) return [];
  const products = rawData.products || [];
  return products.map(g => ({
    id: 'gog_' + g.id,
    title: g.title,
    platform: 'gog',
    type: 'free-to-keep',
    thumbnail: g.coverHorizontal ? 'https:' + g.coverHorizontal : '',
    claimUrl: 'https://www.gog.com' + (g._links?.store?.href || ''),
    availableFrom: null,
    availableUntil: null,
    originalPrice: g.price?.base ? '$' + (g.price.base / 100).toFixed(2) : null,
  }));
}

/**
 * Normalize raw Itch.io items.
 * @param {Array} rawItems
 * @returns {Array}
 */
export function normalizeItchio(rawItems) {
  if (!Array.isArray(rawItems)) return [];
  return rawItems
    .map(item => ({
      id: 'itchio_' + encodeURIComponent(item.link || item.id || ''),
      title: item.title || '',
      platform: 'itchio',
      type: 'free-to-keep',
      thumbnail: item.thumbnail || '',
      claimUrl: item.link || '',
      availableFrom: null,
      availableUntil: null,
      originalPrice: null,
    }))
    .filter(g => g.title && g.claimUrl);
}

/**
 * Normalize a pre-normalized array from the Worker /games endpoint.
 * Handles both legacy GAS field names and current Worker field names.
 * @param {Array} games
 * @returns {Array}
 */
export function normalizeGasResponse(games) {
  if (!Array.isArray(games)) return [];
  const seen = new Set();
  return games
    .map(g => ({
      id: g.id || '',
      title: g.title || 'Unknown',
      platform: g.platform || 'epic',
      type: g.type || 'free-to-keep',
      thumbnail: g.thumbnail || '',
      claimUrl: g.claimUrl || '',
      // Worker returns startDate/endDate (timestamps); GAS returned availableFrom/availableUntil (ISO strings)
      availableFrom:  g.availableFrom  || (g.startDate  ? new Date(g.startDate).toISOString()  : null),
      availableUntil: g.availableUntil || (g.endDate    ? new Date(g.endDate).toISOString()    : null),
      // Worker returns priceOriginal; GAS returned originalPrice
      originalPrice: g.originalPrice || g.priceOriginal || null,
    }))
    .filter(g => {
      if (!g.id || seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });
}
